'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { SectionHeader, MaskedText } from '@/components/ui';
import { InfoPopover } from '@/components/ui/InfoPopover';
import { ModalTrigger } from '@/components/ui/ModalTrigger';
import {
  IconInfo,
  IconNumberOneCircle,
  IconNumberThreeCircle,
  IconNumberTwoCircle,
} from '@repo/svgs';
import { cn } from '@/lib/cn';
import { useAnalytics } from '@/hooks/useAnalytics';
import { toTitleCase } from '@/utils/textTransform';
import { CarouselClientOnly } from '@/components/carousel';
import { ComplaintMonthModal } from './ComplaintMonthModal';

interface ComplaintHistorySectionProps {
  buildingAddress: string;
  isBlurRequired: boolean;
  complaintHistoryData: any | null;
}

type TopComplaint = { type: string; count: number };

const MONTHS: Array<{ full: string; short: string }> = [
  { full: 'January', short: 'Jan' },
  { full: 'February', short: 'Feb' },
  { full: 'March', short: 'Mar' },
  { full: 'April', short: 'Apr' },
  { full: 'May', short: 'May' },
  { full: 'June', short: 'Jun' },
  { full: 'July', short: 'Jul' },
  { full: 'August', short: 'Aug' },
  { full: 'September', short: 'Sep' },
  { full: 'October', short: 'Oct' },
  { full: 'November', short: 'Nov' },
  { full: 'December', short: 'Dec' },
];

function normalizeComplaintHistory(raw: any): {
  byYear: Record<string, Record<string, any[]>>;
  top: Record<string, number>;
} {
  // Expected shape:
  // { complaints: { complaints: { [year]: { [MonthName]: Complaint[] } }, top_complaints: {...} } }
  const root = raw?.complaints ?? raw ?? {};
  // Some backends nest one extra level: complaints.complaints.complaints
  const byYearRaw = (root?.complaints?.complaints ?? root?.complaints ?? {}) as Record<
    string,
    Record<string, any[]>
  >;
  const top = (root?.top_complaints ??
    root?.topComplaints ??
    root?.complaints?.top_complaints ??
    root?.complaints?.topComplaints ??
    {}) as Record<string, number>;
  const byYear = byYearRaw ?? {};
  return { byYear, top };
}

function sortTopComplaints(top: Record<string, number>): TopComplaint[] {
  return Object.entries(top ?? {})
    .map(([type, count]) => ({
      type,
      count: typeof count === 'number' ? count : Number(count) || 0,
    }))
    .filter(x => x.type && Number.isFinite(x.count) && x.count >= 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);
}

function monthCount(yearMap: Record<string, any[]> | undefined, monthFull: string): number {
  if (!yearMap) return 0;

  // 1) exact match (e.g. "January")
  const direct = (yearMap as any)?.[monthFull];
  if (Array.isArray(direct)) return direct.length;

  // 2) case-insensitive match (e.g. "january")
  const target = monthFull.toLowerCase();
  const key =
    Object.keys(yearMap).find(k => k.toLowerCase() === target) ??
    // 3) allow short month keys (e.g. "Jan")
    Object.keys(yearMap).find(k => k.toLowerCase() === target.slice(0, 3));
  const v = key ? (yearMap as any)?.[key] : undefined;
  return Array.isArray(v) ? v.length : 0;
}

function totalForYear(yearMap: Record<string, any[]> | undefined): number {
  if (!yearMap) return 0;
  let total = 0;
  for (const m of MONTHS) total += monthCount(yearMap, m.full);
  return total;
}

function toCssSafeId(input: string): string {
  const slug = (input || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug ? `c-${slug}` : 'c';
}

function NumberCircleIcon({ n }: { n: 1 | 2 | 3 }) {
  const className = 'w-6 h-6 shrink-0';
  if (n === 1) return <IconNumberOneCircle className={className} aria-hidden />;
  if (n === 2) return <IconNumberTwoCircle className={className} aria-hidden />;
  return <IconNumberThreeCircle className={className} aria-hidden />;
}

function getMonthComplaints(yearMap: Record<string, any[]> | undefined, monthFull: string): any[] {
  if (!yearMap) return [];
  const direct = (yearMap as any)?.[monthFull];
  if (Array.isArray(direct)) return direct;

  const target = monthFull.toLowerCase();
  const key =
    Object.keys(yearMap).find(k => k.toLowerCase() === target) ??
    Object.keys(yearMap).find(k => k.toLowerCase() === target.slice(0, 3));
  const v = key ? (yearMap as any)?.[key] : undefined;
  return Array.isArray(v) ? v : [];
}

export function ComplaintHistorySection({
  buildingAddress,
  isBlurRequired,
  complaintHistoryData,
}: ComplaintHistorySectionProps) {
  const analytics = useAnalytics();
  const [isDesktop, setIsDesktop] = useState(false);
  const [isInfoPopoverOpen, setIsInfoPopoverOpen] = useState(false);
  const infoIconRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () => setIsDesktop(window.innerWidth >= 768);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const { byYear, top } = useMemo(
    () => normalizeComplaintHistory(complaintHistoryData),
    [complaintHistoryData],
  );

  const top3 = useMemo(() => sortTopComplaints(top), [top]);
  const topRows = useMemo(() => {
    // In blur mode, render 3 placeholder rows even if we don't have data.
    if (isBlurRequired) return [1, 2, 3] as const;
    return top3;
  }, [isBlurRequired, top3]);

  const [openMonth, setOpenMonth] = useState<{
    year: string;
    month: { full: string; short: string };
    complaints: any[];
  } | null>(null);

  const currentYear = new Date().getFullYear();
  const currentMonthIndex = new Date().getMonth(); // 0-based
  const yearsToShow = useMemo(() => [0, 1, 2, 3].map(i => String(currentYear - i)), [currentYear]);

  const yearCardNodes = useMemo(() => {
    return yearsToShow.map(year => {
      const yearMap = byYear?.[year] ?? undefined;
      const yearTotal = totalForYear(yearMap);

      return {
        id: year,
        content: (
          <div className="rounded-2xl border border-secondary bg-primary p-4">
            <div className="text-body-1-high text-primary text-center">{year}</div>
            <div className="mt-1 text-body-2 text-secondary text-center">
              {isBlurRequired ? (
                <MaskedText className="inline-block w-24 h-4" />
              ) : (
                'Total ' + yearTotal + ' complaints'
              )}
            </div>

            <div className="mt-4 grid grid-cols-4 gap-4">
              {MONTHS.map((m, idx) => {
                const isFutureMonth = Number(year) === currentYear && idx > currentMonthIndex;
                const count = monthCount(yearMap, m.full);

                const display = isBlurRequired ? null : isFutureMonth ? '-' : String(count);
                const isHighlighted = !isBlurRequired && !isFutureMonth && count > 0;
                const isClickable = !isBlurRequired && !isFutureMonth && count > 0;

                return (
                  <button
                    type="button"
                    key={`${year}-${m.full}`}
                    className={cn(
                      'rounded-xl p-2 h-full flex flex-col justify-between text-center',
                      isBlurRequired
                        ? 'bg-secondary'
                        : isHighlighted
                          ? 'bg-caution-low hover:bg-caution-low-hover'
                          : 'bg-transparent',
                      isClickable ? 'cursor-pointer' : 'cursor-default',
                    )}
                    disabled={!isClickable}
                    onClick={() => {
                      if (!isClickable) return;
                      const complaints = getMonthComplaints(yearMap, m.full);
                      setOpenMonth({ year, month: m, complaints });
                      analytics.track('rentalreport_complaints_monthview', {
                        year,
                        month: m.full,
                        complaintCount: complaints.length,
                      });
                    }}
                  >
                    {/* Month label stays visible even when blurred */}
                    <div
                      className={cn(
                        'text-body-2',
                        isFutureMonth
                          ? 'text-tertiary'
                          : isHighlighted
                            ? 'text-primary'
                            : 'text-secondary',
                      )}
                    >
                      {m.short}
                    </div>
                    <div
                      className={cn(
                        'pt-1',
                        isBlurRequired
                          ? 'text-secondary'
                          : isHighlighted
                            ? 'text-caution text-body-2-high'
                            : 'text-secondary text-body-2',
                      )}
                    >
                      {isBlurRequired ? (
                        isFutureMonth ? (
                          <div className="text-body-2 text-tertiary">-</div>
                        ) : (
                          // blur placeholder "bar"
                          <div className="mx-auto h-5 w-3 bg-[#64748B]" />
                        )
                      ) : (
                        display
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ),
      };
    });
  }, [byYear, currentMonthIndex, currentYear, isBlurRequired, yearsToShow]);

  const calendarCarouselId = useMemo(
    () => `complaint-history-calendar-${toCssSafeId(buildingAddress)}`,
    [buildingAddress],
  );

  const hasAnyData = useMemo(() => {
    if (!complaintHistoryData) return false;
    return yearsToShow.some(y => totalForYear(byYear?.[y]) > 0) || top3.length > 0;
  }, [byYear, complaintHistoryData, top3.length, yearsToShow]);

  return (
    <div className="w-full">
      <SectionHeader
        title={`311 complaint history for ${buildingAddress}`}
        trailingIcon={
          isDesktop ? (
            <>
              <button
                ref={infoIconRef}
                type="button"
                aria-label="About 311 complaints"
                className="inline-flex"
                onClick={() => {
                  analytics.track('rentalreport_complaints_tooltip');
                  setIsInfoPopoverOpen(true);
                }}
              >
                <IconInfo className="w-4 h-4 icon-primary cursor-pointer" />
              </button>
              <InfoPopover
                open={isInfoPopoverOpen}
                onOpenChange={setIsInfoPopoverOpen}
                onClose={() => setIsInfoPopoverOpen(false)}
                label="About 311 complaints"
                anchorRef={infoIconRef}
                placement="below"
                align="right"
                offsetY={32}
                offsetX={16}
              >
                <div className="flex flex-col gap-4">
                  <p className="text-body-2 text-primary">
                    A 311 complaint is filed when someone reports an issue with the city. Complaints
                    can be filed by calling 311 or completing a form online.
                  </p>
                </div>
              </InfoPopover>
            </>
          ) : (
            <ModalTrigger
              label="About 311 complaints"
              content={
                <div className="flex flex-col gap-4">
                  <p className="text-body-2 text-primary">
                    A 311 complaint is filed when someone reports an issue with the city. Complaints
                    can be filed by calling 311 or completing a form online.
                  </p>
                </div>
              }
              urlKey="about-311-complaints"
              maxWidth="md"
              onOpen={() => analytics.track('rentalreport_complaints_tooltip')}
            >
              <IconInfo className="w-4 h-4 icon-primary cursor-pointer" />
            </ModalTrigger>
          )
        }
        subtitleText="City data from past 3 years · All open and closed complaints"
      />

      {!hasAnyData && !isBlurRequired ? (
        <div className="md:px-4 px-2">
          <p className="text-body-2 text-primary">
            {buildingAddress} does not have any complaints from the past 3 years.
          </p>
        </div>
      ) : (
        <div className="md:px-4 px-2">
          <div>
            <div className="text-body-2-high text-primary py-3">
              Top{' '}
              {topRows.length == 1
                ? 'most common complaint'
                : topRows.length + ' most common complaints'}
            </div>

            <div>
              <div>
                {topRows.map((row, idx) => {
                  const c = typeof row === 'number' ? null : row;
                  const key = c ? c.type : `blur-${idx + 1}`;
                  const n = (idx + 1) as 1 | 2 | 3;
                  return (
                    <div key={key}>
                      <div className="flex items-center gap-4 py-4">
                        <NumberCircleIcon n={n} />
                        <div className="min-w-0">
                          {isBlurRequired ? (
                            <div className="space-y-2">
                              <MaskedText className="w-44 h-4" />
                              <MaskedText className="w-28 h-3" />
                            </div>
                          ) : c ? (
                            <>
                              <div className="text-body-2 text-primary truncate">
                                {toTitleCase(c.type)}
                              </div>
                              <div className="text-caption text-secondary pt-1">
                                {c.count} complaint{c.count === 1 ? '' : 's'}
                              </div>
                            </>
                          ) : null}
                        </div>
                      </div>
                      {idx < topRows.length - 1 ? (
                        <div className="border-b border-secondary" />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-4">
            {/* Mobile: edge-to-end carousel */}
            <div className="sm:hidden">
              <CarouselClientOnly
                className="col-span-full -mx-6"
                carouselId={calendarCarouselId}
                items={yearCardNodes}
                perView={{ mobile: 1, tablet: 1, desktop: 1 }}
                peek={{ mobile: 12, tablet: 0, desktop: 0 }}
                padStart={{ mobile: 24, tablet: 0, desktop: 0 }}
                padEnd={{ mobile: 24, tablet: 0, desktop: 0 }}
                gap={16}
                ariaLabel="311 complaint history calendar"
              />
            </div>

            {/* Desktop: 2x2 grid */}
            <div className="hidden sm:grid md:grid-cols-2 gap-4">
              {yearCardNodes.map(item => (
                <div key={item.id}>{item.content}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {openMonth ? (
        <ComplaintMonthModal
          open
          year={openMonth.year}
          month={openMonth.month}
          complaints={openMonth.complaints}
          onClose={() => {
            setOpenMonth(null);
            analytics.track('rentalreport_complaints_monthview_exit');
          }}
        />
      ) : null}
    </div>
  );
}
